import React, { useState, useEffect, useCallback } from 'react';

/**
 * Confetti Component
 *
 * Premium celebration animations for delightful moments.
 * Features realistic physics, multiple particle types, and smooth animations.
 */

// Premium color palette
const COLORS = [
  '#F43F5E', // coral
  '#FB7185', // light coral
  '#FBBF24', // gold
  '#FCD34D', // light gold
  '#34D399', // emerald
  '#A78BFA', // violet
  '#60A5FA', // blue
  '#F472B6', // pink
];

const SHAPES = ['square', 'circle', 'ribbon', 'star'];

/**
 * Generate a single confetti particle with realistic physics
 */
function generateParticle(index, originX, originY) {
  const angle = (Math.random() * 120 + 210) * (Math.PI / 180); // Spread upward
  const velocity = 12 + Math.random() * 12;
  const size = 6 + Math.random() * 10;

  return {
    id: index,
    x: originX || 50,
    y: originY || 50,
    vx: Math.cos(angle) * velocity * (Math.random() > 0.5 ? 1 : -1),
    vy: Math.sin(angle) * velocity - 15, // Strong upward boost
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 30,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
    size,
    opacity: 1,
    wobble: Math.random() * Math.PI * 2,
    wobbleSpeed: 0.1 + Math.random() * 0.1,
  };
}

/**
 * Render particle based on shape
 */
function ParticleShape({ particle }) {
  const baseStyle = {
    left: `${particle.x}%`,
    top: `${particle.y}%`,
    width: particle.size,
    height: particle.shape === 'ribbon' ? particle.size * 2 : particle.size,
    transform: `rotate(${particle.rotation}deg)`,
    opacity: particle.opacity,
  };

  if (particle.shape === 'star') {
    return (
      <svg
        className="absolute"
        style={{
          ...baseStyle,
          width: particle.size * 1.5,
          height: particle.size * 1.5,
        }}
        viewBox="0 0 24 24"
      >
        <path
          fill={particle.color}
          d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z"
        />
      </svg>
    );
  }

  if (particle.shape === 'ribbon') {
    return (
      <div
        className="absolute"
        style={{
          ...baseStyle,
          backgroundColor: particle.color,
          borderRadius: '2px',
          transform: `rotate(${particle.rotation}deg) scaleX(0.3)`,
        }}
      />
    );
  }

  return (
    <div
      className="absolute"
      style={{
        ...baseStyle,
        backgroundColor: particle.color,
        borderRadius: particle.shape === 'circle' ? '50%' : '2px',
      }}
    />
  );
}

/**
 * Confetti burst animation
 */
export function Confetti({
  active = false,
  count = 50,
  duration = 3000,
  originX = 50,
  originY = 50,
  spread = 'wide', // 'narrow' | 'wide' | 'burst'
  onComplete,
  className = '',
}) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!active) {
      setParticles([]);
      return;
    }

    // Generate particles
    const particleCount = spread === 'burst' ? count * 1.5 : count;
    const newParticles = Array.from({ length: particleCount }, (_, i) =>
      generateParticle(i, originX, originY)
    );
    setParticles(newParticles);

    // Animation loop
    let animationFrame;
    let startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;

      if (progress >= 1) {
        setParticles([]);
        onComplete?.();
        return;
      }

      setParticles((prev) =>
        prev.map((p) => ({
          ...p,
          x: p.x + p.vx * 0.4 + Math.sin(p.wobble) * 0.3,
          y: p.y + p.vy * 0.4,
          vy: p.vy + 0.4, // Gravity
          vx: p.vx * 0.99, // Air resistance
          rotation: p.rotation + p.rotationSpeed,
          wobble: p.wobble + p.wobbleSpeed,
          opacity: Math.max(0, 1 - Math.pow(progress, 0.5)),
        }))
      );

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [active, count, duration, originX, originY, spread, onComplete]);

  if (particles.length === 0) return null;

  return (
    <div
      className={`fixed inset-0 pointer-events-none overflow-hidden ${className}`}
      style={{ zIndex: 9999 }}
    >
      {particles.map((particle) => (
        <ParticleShape key={particle.id} particle={particle} />
      ))}
    </div>
  );
}

/**
 * useConfetti hook - Trigger confetti programmatically
 */
export function useConfetti() {
  const [isActive, setIsActive] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [config, setConfig] = useState({ count: 50, spread: 'wide' });

  const trigger = useCallback((x = 50, y = 50, count = 50, spread = 'wide') => {
    setOrigin({ x, y });
    setConfig({ count, spread });
    setIsActive(true);
  }, []);

  const handleComplete = useCallback(() => {
    setIsActive(false);
  }, []);

  const ConfettiComponent = useCallback(() => (
    <Confetti
      active={isActive}
      originX={origin.x}
      originY={origin.y}
      count={config.count}
      spread={config.spread}
      onComplete={handleComplete}
    />
  ), [isActive, origin, config, handleComplete]);

  return { trigger, Confetti: ConfettiComponent };
}

/**
 * Sparkle animation for subtle celebration
 */
export function Sparkle({
  active = false,
  x = 50,
  y = 50,
  size = 24,
  color = '#FBBF24',
  className = '',
}) {
  const [visible, setVisible] = useState(active);

  useEffect(() => {
    if (active) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [active]);

  if (!visible) return null;

  return (
    <div
      className={`absolute pointer-events-none animate-bounce-in ${className}`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className="animate-spin-slow"
      >
        <defs>
          <linearGradient id="sparkleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor="#FFD700" />
          </linearGradient>
        </defs>
        <path
          fill="url(#sparkleGradient)"
          d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"
        />
      </svg>
    </div>
  );
}

/**
 * Multiple sparkles burst
 */
export function SparklesBurst({ active, count = 5, duration = 1500 }) {
  const [sparkles, setSparkles] = useState([]);

  useEffect(() => {
    if (!active) {
      setSparkles([]);
      return;
    }

    const newSparkles = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 30 + Math.random() * 40,
      y: 20 + Math.random() * 40,
      size: 16 + Math.random() * 16,
      delay: Math.random() * 500,
    }));
    setSparkles(newSparkles);

    const timer = setTimeout(() => setSparkles([]), duration);
    return () => clearTimeout(timer);
  }, [active, count, duration]);

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute animate-bounce-in"
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`,
            animationDelay: `${sparkle.delay}ms`,
          }}
        >
          <svg
            width={sparkle.size}
            height={sparkle.size}
            viewBox="0 0 24 24"
            className="animate-spin-slow"
          >
            <path
              fill={COLORS[sparkle.id % COLORS.length]}
              d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"
            />
          </svg>
        </div>
      ))}
    </div>
  );
}

/**
 * CelebrationOverlay - Full screen celebration with premium design
 */
export function CelebrationOverlay({
  active,
  title,
  message,
  emoji = '🎉',
  onDismiss,
  className = '',
}) {
  const { trigger, Confetti: ConfettiComponent } = useConfetti();
  const [showSparkles, setShowSparkles] = useState(false);

  useEffect(() => {
    if (active) {
      // Trigger confetti from multiple points
      trigger(50, 30, 60, 'burst');
      setTimeout(() => trigger(30, 40, 30, 'narrow'), 200);
      setTimeout(() => trigger(70, 40, 30, 'narrow'), 400);
      setShowSparkles(true);
    } else {
      setShowSparkles(false);
    }
  }, [active, trigger]);

  if (!active) return null;

  return (
    <>
      <ConfettiComponent />
      <SparklesBurst active={showSparkles} count={8} />
      <div
        className={`
          fixed inset-0
          bg-gradient-to-b from-black/60 to-black/40
          backdrop-blur-sm
          flex items-center justify-center
          animate-fade-in
          ${className}
        `}
        style={{ zIndex: 9998 }}
        onClick={onDismiss}
      >
        <div
          className="
            bg-white rounded-3xl p-8 mx-4 text-center max-w-sm
            shadow-2xl
            animate-bounce-in
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* Animated emoji */}
          <div className="relative inline-block mb-4">
            <div className="text-7xl animate-float">{emoji}</div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-gold-400 to-amber-500 rounded-full animate-pulse flex items-center justify-center">
              <span className="text-white text-xs">✓</span>
            </div>
          </div>

          {title && (
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {title}
            </h2>
          )}
          {message && (
            <p className="text-slate-500 mb-6 leading-relaxed">
              {message}
            </p>
          )}
          <button
            onClick={onDismiss}
            className="
              w-full py-4 rounded-2xl
              bg-gradient-to-r from-coral-500 to-coral-600
              text-white font-semibold text-lg
              shadow-xl shadow-coral-500/30
              hover:shadow-2xl hover:scale-[1.02]
              active:scale-[0.98]
              transition-all duration-300
            "
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );
}

export default Confetti;
